import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    TextField,
    Button,
    Link,
    CircularProgress,
    IconButton,
    InputAdornment,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Grid from '@mui/material/Grid2';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import axios from 'axios';

const ResetPasswordPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const API_URL = process.env.REACT_APP_API_URL;

    const query = new URLSearchParams(location.search);
    const token = query.get('token');

    // Initialize CSRF token
    useEffect(() => {
        const API_URL = process.env.REACT_APP_API_URL;
        const fetchData = async () => {
            await fetch(`${API_URL}/welcome`, {
                method: 'GET',
                credentials: 'include',
            });
        };
        fetchData();
    }, []);

    // Guard: no token in URL
    if (!token) {
        return (
            <Grid container sx={{ minHeight: '100vh', width: '100%' }}>
                <Grid
                    size={{ xs: 12, md: 6 }}
                    sx={{
                        background: `linear-gradient(rgba(11, 21, 55, 0.75), rgba(11, 21, 55, 0.75)), url('/Moonlight-Connect-login-background.svg') #0B1537`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: isMobile ? '200px' : '100vh',
                        p: 4,
                    }}
                >
                    <Box
                        component="img"
                        sx={{
                            width: isMobile ? '280px' : isTablet ? '500px' : '750px',
                        }}
                        src="/MOONLIGHT-quality-logo.svg"
                        alt="Moonlight Quality"
                    />
                </Grid>
                <Grid
                    size={{ xs: 12, md: 6 }}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: { xs: 3, sm: 4, md: 6 },
                        background: '#F3F3F3',
                    }}
                >
                    <Box sx={{ textAlign: 'center', width: '100%', maxWidth: '400px' }}>
                        <Typography
                            color="error"
                            sx={{ mb: 3, fontWeight: 600, fontSize: '1.1rem' }}
                        >
                            Invalid or missing reset token. Please request a new password reset
                            link.
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={() => navigate('/forgot-password')}
                            fullWidth
                            sx={{
                                background: '#FBBB1D',
                                height: '56px',
                                mb: 2,
                                fontFamily: 'Montserrat, sans-serif',
                                fontWeight: 700,
                                fontSize: { xs: 16, sm: 18 },
                                borderRadius: '10px',
                                boxShadow: 5,
                                color: '#fff',
                                '&:hover': {
                                    background: '#E5AA15',
                                },
                            }}
                        >
                            REQUEST NEW LINK
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        );
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (newPassword.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        setIsLoading(true);

        try {
            const response = await axios.post(
                `${API_URL}/forgot-password/reset`,
                {
                    token: token,
                    newPassword: newPassword,
                },
                {
                    withCredentials: true,
                    withXSRFToken: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );
            setSuccessMessage(response.data.message);
            setIsSuccess(true);
        } catch (error) {
            const message = error.response?.data?.message || 'An error occurred. Please try again.';
            setError(message);
        } finally {
            setIsLoading(false);
            setNewPassword('');
            setConfirmPassword('');
        }
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const passwordAdornment = (
        <InputAdornment position="end">
            <IconButton
                aria-label="toggle password visibility"
                onClick={togglePasswordVisibility}
                edge="end"
            >
                {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
        </InputAdornment>
    );

    const inputSx = {
        backgroundColor: '#FFFFFF',
        borderRadius: '10px',
        boxShadow: 5,
        '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            '& fieldset': { borderColor: 'transparent' },
            '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.15)' },
            '&.Mui-focused fieldset': { borderColor: 'rgba(0,0,0,0.25)', borderWidth: '1px' },
        },
        input: { fontSize: '16px' },
    };

    return (
        <Grid container sx={{ minHeight: '100vh', width: '100%' }}>
            {/* Left panel - Gradient background with logo */}
            <Grid
                size={{ xs: 12, md: 6 }}
                sx={{
                    background: `linear-gradient(rgba(11, 21, 55, 0.75), rgba(11, 21, 55, 0.75)), url('/Moonlight-Connect-login-background.svg') #0B1537`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: isMobile ? '200px' : '100vh',
                    p: 4,
                }}
            >
                <Box
                    component="img"
                    sx={{
                        width: isMobile ? '280px' : isTablet ? '500px' : '750px',
                    }}
                    src="/MOONLIGHT-quality-logo.svg"
                    alt="Moonlight Quality"
                />
            </Grid>

            {/* Right panel - Reset password form */}
            <Grid
                size={{ xs: 12, md: 6 }}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: { xs: 3, sm: 4, md: 6 },
                    background: '#F3F3F3',
                }}
            >
                <Box
                    sx={{
                        textAlign: 'left',
                        width: '100%',
                        maxWidth: '400px',
                    }}
                >
                    <Typography
                        variant="h2"
                        sx={{
                            mb: 1,
                            fontFamily: 'Montserrat, sans-serif',
                            fontWeight: 700,
                            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                        }}
                    >
                        RESET PASSWORD
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            mb: 3,
                            color: '#545454',
                        }}
                    >
                        Enter your new password below.
                    </Typography>
                </Box>

                {!isSuccess ? (
                    <Box
                        component="form"
                        onSubmit={handleSubmit}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            maxWidth: '400px',
                        }}
                    >
                        <TextField
                            placeholder="New Password"
                            type={showPassword ? 'text' : 'password'}
                            value={newPassword}
                            onChange={(e) => {
                                setNewPassword(e.target.value);
                                if (error) setError('');
                            }}
                            fullWidth
                            margin="normal"
                            autoFocus
                            slotProps={{
                                input: {
                                    endAdornment: passwordAdornment,
                                },
                            }}
                            sx={inputSx}
                        />
                        <TextField
                            placeholder="Confirm Password"
                            type={showPassword ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={(e) => {
                                setConfirmPassword(e.target.value);
                                if (error) setError('');
                            }}
                            fullWidth
                            margin="normal"
                            slotProps={{
                                input: {
                                    endAdornment: passwordAdornment,
                                },
                            }}
                            sx={inputSx}
                        />

                        {error && (
                            <Typography
                                color="error"
                                sx={{
                                    mt: 1,
                                    textAlign: 'left',
                                }}
                            >
                                {error}
                            </Typography>
                        )}

                        <Button
                            type="submit"
                            variant="contained"
                            disabled={isLoading || !newPassword.trim() || !confirmPassword.trim()}
                            fullWidth
                            sx={{
                                background: '#FBBB1D',
                                height: '56px',
                                mt: 3,
                                mb: 2,
                                fontFamily: 'Montserrat, sans-serif',
                                fontWeight: 700,
                                fontSize: { xs: 16, sm: 18, md: 20 },
                                borderRadius: '10px',
                                boxShadow: 5,
                                color: '#fff',
                                '&:hover': {
                                    background: '#E5AA15',
                                },
                                '&.Mui-disabled': {
                                    background: '#FBBB1D',
                                    opacity: 0.7,
                                    color: '#fff',
                                },
                            }}
                        >
                            {isLoading ? (
                                <CircularProgress size={28} sx={{ color: '#fff' }} />
                            ) : (
                                'RESET PASSWORD'
                            )}
                        </Button>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            width: '100%',
                            maxWidth: '400px',
                            textAlign: 'center',
                        }}
                    >
                        <Typography
                            sx={{
                                mb: 3,
                                color: '#10B981',
                                fontWeight: 600,
                                fontSize: '1.1rem',
                            }}
                        >
                            {successMessage}
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={() => navigate('/')}
                            fullWidth
                            sx={{
                                background: '#FBBB1D',
                                height: '56px',
                                mb: 2,
                                fontFamily: 'Montserrat, sans-serif',
                                fontWeight: 700,
                                fontSize: { xs: 16, sm: 18 },
                                borderRadius: '10px',
                                boxShadow: 5,
                                color: '#fff',
                                '&:hover': {
                                    background: '#E5AA15',
                                },
                            }}
                        >
                            BACK TO SIGN IN
                        </Button>
                    </Box>
                )}

                {!isSuccess && (
                    <Box
                        sx={{
                            textAlign: 'left',
                            width: '100%',
                            maxWidth: '400px',
                            mt: 1,
                        }}
                    >
                        <Link
                            href="/"
                            underline="hover"
                            sx={{
                                color: '#545454',
                                fontFamily: 'Montserrat, sans-serif',
                                fontSize: '0.875rem',
                                cursor: 'pointer',
                            }}
                        >
                            Back to Sign In
                        </Link>
                    </Box>
                )}
            </Grid>
        </Grid>
    );
};

export default ResetPasswordPage;
